/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useMemo } from 'react';
import { Row, Col, Divider, Tooltip } from 'antd';
import { formatString, interestRateByPortfolio } from 'utils';
import { Card, Information } from 'components/gyramais';
import { Progress } from 'components/sdk';
import useWindowDimensions from 'utils/hooks/useWindowDimensions';
import { useBusinessLoanContext } from 'contexts/BusinessLoanContext';

import './styles.less';

/**
 *
 * @param { 'Badesul Microcrédito' | 'Badesul MPE' } portfolioName
 */
function getDataByPortfolio(portfolioName, loan) {
  switch (portfolioName) {
    case 'Badesul Microcrédito':
      return {
        installmentsText: '12 parcelas mensais de amortização',
        fee: <div>{interestRateByPortfolio[portfolioName]} a.m.</div>,
        minAmount: 1000,
        maxAmount: 5000,
        installmentCount: 12,
        paymentDay: 10,
      };

    case 'Badesul MPE':
      return {
        installmentsText: `2 trimestrais de carência e 54 mensais de amortização`,
        fee: loan?.additionalInfo?.taxRateType ?? '-',
        minAmount: 20000,
        maxAmount: loan?.finalAmount || 500000,
        installmentCount: loan?.finalInstallmentsCount || 0,
        paymentDay: 15,
      };

    default:
      throw new Error('Portfolio não mapeado');
  }
}

/**
 *
 * @param {{
 *  loan: any
 *  onChangeValues?: void
 *  disabled?: boolean
 *  type: 'danger' | 'warning' | 'success'
 *  className?: string
 *  maxInstallmentsCount?: number
 * }} props
 * @returns {React.FC}
 */
const PreApprovedBadesulCard = ({ className, type = 'danger' }) => {
  const { currentContext } = useBusinessLoanContext();
  const { width } = useWindowDimensions();

  const { loan } = currentContext;

  const loading = false;

  const preApprovedProgress = useMemo(() => {
    const percentage = {
      danger: 10,
      warning: 50,
      success: 100,
    };

    return (
      <div className="pre-approved-progress-container">
        <Progress
          className="pre-approved-progress"
          type="circle"
          percent={percentage[type]}
          progressColor={type}
          strokeWidth={9}
        />

        {/* eslint-disable-next-line react/no-unknown-property */}
        <div className="pre-approved-progress-content" align="middle">
          <p className="no-margin-bottom pre-approved-progress-label secondary-text">
            Valor
            <br />
            disponível
          </p>

          <h5>
            <strong>{formatString(loan?.amount, 'currency')?.split('.')[0]} mil</strong>
          </h5>
        </div>
      </div>
    );
  }, [type, loan]);

  const values = getDataByPortfolio(loan?.portfolio?.name, loan);

  return (
    <div>
      <Card className={`pre-approved-card ${className || ''}`}>
        <Row gutter={[56, 16]} align="middle">
          <Col xs={24} sm={24} md={11}>
            <Row align="middle" gutter={[16, 0]}>
              <Col xs={12} sm={12} md={0}>
                {preApprovedProgress}
              </Col>

              <Col xs={12} sm={12} md={24}>
                <Row gutter={width > 768 ? [32, 24] : 8}>
                  <Col xs={24} sm={24} md={24}>
                    <Information
                      loading={loading}
                      label="Total pré-aprovado"
                      value={formatString(loan?.finalAmount, 'currency')}
                      type=""
                    />
                  </Col>

                  <Col xs={24} sm={24} md={24}>
                    <Tooltip
                      title="A taxa pode sofrer alteração, o valor será definido no momento da contratação"
                      placement="top"
                    >
                      <Information loading={loading} label="Taxa" value={values.fee} />
                    </Tooltip>
                  </Col>
                  <Col xs={24} sm={24} md={24}>
                    <Information
                      loading={loading}
                      label="Parcelas"
                      value={values.installmentsText}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col xs={24} sm={24} md={2} align="middle">
            <Divider
              type={width > 768 && 'vertical'}
              className={width > 768 ? 'pre-approved-vertical-divider' : ''}
            />
          </Col>

          <Col xs={24} sm={24} md={11}>
            <Row gutter={[56, 16]} align="middle">
              <Col span={24}>
                <Information
                  loading={loading}
                  label="Prazo"
                  value={`${values.installmentCount} meses`}
                  type=""
                />
              </Col>

              <Col span={24}>
                <Information
                  loading={loading}
                  label="Data de vencimento"
                  value={`Todo dia ${values.paymentDay || '-'}`}
                  type=""
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <div className="badesul-mpe-pre-approved-disclaimer">
        Financiamento possui taxa de análise (0,8% do valor contratado) que deve ser paga antes da
        assinatura da CCB (Cédula de Crédito Bancário)
        <br />
        Financiamento com incidência de IOF e desconto da taxa de contratação do fundo garantidor
        FGI PEAC
      </div>
    </div>
  );
};

export default PreApprovedBadesulCard;
